import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import type { CompanyRow } from "../../lib/crm-types";
import { cn } from "../ui/cn";

/**
 * Find one company by name, in place, without leaving the page.
 *
 * Written because grouping had no way to name a company that was not already
 * on screen. The duplicate card could only offer the two sides of its own
 * flag, and the group panel offered nothing at all -- so "put this depot under
 * CEF" was unsayable unless the queue happened to have flagged that exact pair.
 *
 * Deliberately not `CommandPalette`, the ⌘K overlay: that one exists to
 * *navigate*, takes over the screen, answers with all four products' worth of
 * things and ends by leaving the page you are on. Here the answer is a company
 * and a company only, and it is a value in a form somebody is half way through
 * filling in.
 *
 * `canBeGroup` asks the server for top-level companies only, rather than
 * filtering the page here: a page of fifty results filtered down to four looks
 * like a search that found nothing.
 */
export function CompanyPicker({
  placeholder = "Search companies…",
  canBeGroup = false,
  exclude = [],
  allow,
  onPick,
}: {
  placeholder?: string;
  /** Only companies that could be given branches. */
  canBeGroup?: boolean;
  /** Ids never worth offering -- the company being filed, its group already. */
  exclude?: number[];
  /** A further condition the server has no parameter for. */
  allow?: (company: CompanyRow) => boolean;
  onPick: (company: CompanyRow) => void;
}) {
  const [term, setTerm] = useState("");

  /* Debounced for the same reason the ⌘K search is: a fast typist should make
     one request, not nine. */
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 180);
    return () => clearTimeout(timer);
  }, [term]);

  const results = useQuery({
    queryKey: queryKeys.crmCompanies({ q: debounced, canBeGroup, limit: 8 }),
    queryFn: () => crmApi.companies({ q: debounced, canBeGroup, limit: 8 }),
    enabled: debounced.length >= 2,
    staleTime: 30_000,
  });

  const rows = (results.data?.companies ?? [])
    .filter((company) => !exclude.includes(company.id))
    .filter((company) => allow?.(company) ?? true);

  return (
    <div className="flex flex-col gap-1">
      <input
        type="search"
        value={term}
        placeholder={placeholder}
        onChange={(event) => setTerm(event.target.value)}
        className="h-8 rounded-md border border-line bg-app px-2 text-xs text-content outline-none focus:border-brand-400"
      />

      {debounced.length < 2 ? null : results.isLoading ? (
        <p className="px-1 text-[11px] text-faint">Searching…</p>
      ) : rows.length === 0 ? (
        <p className="px-1 text-[11px] text-faint">
          Nothing matching “{debounced}”.
        </p>
      ) : (
        <ul className="max-h-40 overflow-y-auto rounded-md border border-line">
          {rows.map((company) => (
            <li key={company.id}>
              <button
                type="button"
                onClick={() => {
                  onPick(company);
                  setTerm("");
                }}
                className={cn(
                  "flex w-full flex-wrap items-baseline gap-x-2 px-2 py-1 text-left text-xs",
                  "text-content hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
                )}
              >
                <span className="font-medium">{company.name}</span>
                <span className="text-[11px] text-faint">
                  {/* The branch count first: it is what tells somebody looking
                      for a group that this is one already. */}
                  {[
                    company.branchCount > 0
                      ? `${company.branchCount} branches`
                      : null,
                    company.domain,
                    company.city,
                  ]
                    .filter(Boolean)
                    .join(" · ") || `#${company.id}`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
